import { useMemo, useRef, useState } from 'react';
import { Icon } from '../../../layout/Icon.jsx';
import { Pill } from '../../../layout/Pill.jsx';
import { formatIQD, formatDateTime } from '../../../shared/format.js';
import { PrintLogo } from '../../../shared/PrintLogo.jsx';
import { ExportButton } from '../../reports/components/ExportButton.jsx';
import { useSupplierStatement } from '../hooks/useSupplierAccounts.js';
import { Modal } from './Modal.jsx';
import './print.css';

const TYPES = [
  { key: '', label: 'كل الحركات' },
  { key: 'purchase_invoice', label: 'فواتير المشتريات' },
  { key: 'payment', label: 'التسديدات' },
  { key: 'refund', label: 'الاستردادات' },
  { key: 'adjustment', label: 'التسويات' },
  { key: 'cancellation', label: 'الإلغاءات' }
];

function balanceLabel(v) {
  if (v > 0) return { text: formatIQD(v), color: '#B91C1C', hint: 'علينا للمورد' };
  if (v < 0) return { text: formatIQD(-v), color: '#15803D', hint: 'رصيد دائن لنا' };
  return { text: formatIQD(0), color: '#15803D', hint: 'الحساب مسوّى' };
}

/** كشف حساب المورد — كل حركة بالرصيد التراكمي بعدها، مع الطباعة والتصدير. */
export function StatementModal({ supplierId, supplierName, onClose, onSettle }) {
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [type, setType] = useState('');
  const [q, setQ] = useState('');
  const [search, setSearch] = useState('');
  const tableRef = useRef(null);

  const params = useMemo(() => {
    const p = {};
    if (from) p.from = from;
    if (to) p.to = to;
    if (type) p.type = type;
    if (search.trim()) p.q = search.trim();
    return p;
  }, [from, to, type, search]);

  const query = useSupplierStatement(supplierId, params);
  const data = query.data;
  const rows = data?.rows || [];
  const opening = Number(data?.opening || 0);
  const closing = Number(data?.closing || 0);
  const hasRange = !!(from || to);

  const exportRows = rows.map((r) => ({
    at: formatDateTime(r.at),
    typeLabel: r.typeLabel,
    document: r.document,
    invoiceTotal: r.invoiceTotal || 0,
    paid: r.paid || 0,
    due: r.due || 0,
    balanceAfter: r.balanceAfter,
    cashboxName: r.cashboxName || '',
    userName: r.userName || '',
    notes: r.notes || ''
  }));

  const columns = [
    { key: 'at', label: 'التاريخ والوقت' },
    { key: 'typeLabel', label: 'نوع الحركة' },
    { key: 'document', label: 'رقم المستند' },
    { key: 'invoiceTotal', label: 'إجمالي الفاتورة' },
    { key: 'paid', label: 'المدفوع' },
    { key: 'due', label: 'المستحق' },
    { key: 'balanceAfter', label: 'الرصيد التراكمي' },
    { key: 'cashboxName', label: 'القاصة' },
    { key: 'userName', label: 'المستخدم' },
    { key: 'notes', label: 'الملاحظات' }
  ];

  const open = balanceLabel(opening);
  const close = balanceLabel(closing);

  return (
    <Modal
      title={`كشف حساب — ${supplierName || data?.supplier?.name || ''}`}
      sub={data?.supplier?.phone ? `هاتف: ${data.supplier.phone}` : 'كل حركات الحساب مع الرصيد التراكمي'}
      width={1180}
      onClose={onClose}
      footer={(
        <>
          <button type="button" className="btn flex-1 justify-center" onClick={onClose}>إغلاق</button>
          {onSettle && closing > 0 ? (
            <button type="button" className="btn btn-primary flex-1 justify-center" onClick={onSettle}>
              <Icon name="check" size={14} /> تسديد
            </button>
          ) : null}
        </>
      )}
    >
      <div className="flex items-center gap-2 flex-wrap no-print">
        <label className="text-[12px] text-ink-3">من</label>
        <input type="date" className="input" style={{ width: 150 }} value={from} onChange={(e) => setFrom(e.target.value)} />
        <label className="text-[12px] text-ink-3">إلى</label>
        <input type="date" className="input" style={{ width: 150 }} value={to} onChange={(e) => setTo(e.target.value)} />
        <select className="input" style={{ width: 170 }} value={type} onChange={(e) => setType(e.target.value)}>
          {TYPES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
        </select>
        <form
          className="flex items-center gap-1"
          onSubmit={(e) => { e.preventDefault(); setSearch(q); }}
        >
          <input
            className="input"
            style={{ width: 200 }}
            placeholder="بحث برقم الفاتورة أو السند..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          <button type="submit" className="btn"><Icon name="search" size={13} /></button>
        </form>
        {(from || to || type || search) ? (
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => { setFrom(''); setTo(''); setType(''); setQ(''); setSearch(''); }}
          >
            <Icon name="x" size={13} /> مسح الفلاتر
          </button>
        ) : null}
        <div className="ms-auto flex items-center gap-2">
          <ExportButton
            rows={exportRows}
            columns={columns}
            filename={`كشف-حساب-${(supplierName || 'مورد').replace(/\s+/g, '-')}`}
            title={`كشف حساب المورد — ${supplierName || ''}`}
            tableRef={tableRef}
          />
          <button type="button" className="btn" onClick={() => window.print()}>
            <Icon name="print" size={13} /> طباعة
          </button>
        </div>
      </div>

      <div className="print-root">
        {/* شعار المركز من الإعدادات — أعلى الورقة ووسطها */}
        <PrintLogo />
        <div className="print-section text-center mb-2">
          <h2 className="text-[17px] font-extrabold m-0">كشف حساب المورد</h2>
          <div className="text-[12.5px] text-ink-2 mt-0.5">
            {data?.supplier?.name || supplierName}
            {data?.supplier?.phone ? ` — ${data.supplier.phone}` : ''}
          </div>
          <div className="text-[12px] text-ink-3 mt-0.5">
            {hasRange
              ? `المدة: من ${from || 'البداية'} إلى ${to || 'اليوم'}`
              : 'المدة: كل الحركات منذ بداية التعامل'}
          </div>
        </div>

        <div className="grid grid-cols-4 gap-2 print-section mb-2">
          <div className="card p-2.5 kpi-card">
            <div className="text-[11px] text-ink-3">الرصيد الافتتاحي</div>
            <div className="text-[15px] font-extrabold mt-0.5" style={{ color: open.color }}>{open.text}</div>
            <div className="text-[10.5px] text-ink-3">{open.hint}</div>
          </div>
          <div className="card p-2.5 kpi-card">
            <div className="text-[11px] text-ink-3">مشتريات المدة</div>
            <div className="text-[15px] font-extrabold mt-0.5" style={{ color: '#1D4ED8' }}>
              {formatIQD(data?.totals?.purchasesTotal || 0)}
            </div>
          </div>
          <div className="card p-2.5 kpi-card">
            <div className="text-[11px] text-ink-3">المسدَّد خلال المدة</div>
            <div className="text-[15px] font-extrabold mt-0.5" style={{ color: '#15803D' }}>
              {formatIQD((data?.totals?.paidAtPurchase || 0) + (data?.totals?.paymentsTotal || 0))}
            </div>
          </div>
          <div className="card p-2.5 kpi-card">
            <div className="text-[11px] text-ink-3">الرصيد الختامي</div>
            <div className="text-[15px] font-extrabold mt-0.5" style={{ color: close.color }}>{close.text}</div>
            <div className="text-[10.5px] text-ink-3">{close.hint}</div>
          </div>
        </div>

        <div className="overflow-x-auto" ref={tableRef}>
          <table className="w-full text-[12.5px]">
            <thead>
              <tr className="text-ink-3 border-b border-line text-right">
                <th className="p-2">التاريخ والوقت</th>
                <th className="p-2">نوع الحركة</th>
                <th className="p-2">رقم المستند</th>
                <th className="p-2">إجمالي الفاتورة</th>
                <th className="p-2">المدفوع</th>
                <th className="p-2">المستحق</th>
                <th className="p-2">الرصيد التراكمي</th>
                <th className="p-2">القاصة</th>
                <th className="p-2">المستخدم</th>
                <th className="p-2">الملاحظات</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-line" style={{ background: '#FAF6EE' }}>
                <td className="p-2 font-bold" colSpan={6}>الرصيد الافتتاحي</td>
                <td className="p-2 font-extrabold" style={{ color: open.color }}>{formatIQD(opening)}</td>
                <td className="p-2" colSpan={3}>{open.hint}</td>
              </tr>
              {query.isLoading ? (
                <tr><td colSpan={10} className="text-center py-8 text-ink-3">جاري التحميل...</td></tr>
              ) : !rows.length ? (
                <tr><td colSpan={10} className="text-center py-8 text-ink-3">لا حركات في هذه المدة</td></tr>
              ) : rows.map((r, i) => (
                <tr key={`${r.kind}-${r.document}-${i}`} className="border-b border-line">
                  <td className="p-2 text-ink-3">{formatDateTime(r.at)}</td>
                  <td className="p-2">
                    {r.kind === 'purchase_invoice' ? <Pill variant="info">{r.typeLabel}</Pill>
                      : r.kind === 'payment' ? <Pill variant="success">{r.typeLabel}</Pill>
                        : r.kind === 'refund' ? <Pill variant="orange">{r.typeLabel}</Pill>
                          : r.kind === 'cancellation' ? <Pill variant="danger">{r.typeLabel}</Pill>
                            : <Pill variant="muted">{r.typeLabel}</Pill>}
                  </td>
                  <td className="p-2 font-mono">{r.document}</td>
                  <td className="p-2">{r.invoiceTotal ? formatIQD(r.invoiceTotal) : '—'}</td>
                  <td className="p-2 text-green font-semibold">{r.paid ? formatIQD(r.paid) : '—'}</td>
                  <td className="p-2">{r.due ? formatIQD(r.due) : '—'}</td>
                  <td
                    className="p-2 font-extrabold"
                    style={{ color: r.balanceAfter > 0 ? '#B91C1C' : '#15803D' }}
                  >
                    {formatIQD(r.balanceAfter)}
                  </td>
                  <td className="p-2 text-ink-3">{r.cashboxName || '—'}</td>
                  <td className="p-2 text-ink-3">{r.userName || '—'}</td>
                  <td className="p-2 text-ink-3">
                    {r.reversed ? <Pill variant="danger">سند معكوس</Pill> : (r.notes || '—')}
                  </td>
                </tr>
              ))}
              <tr style={{ background: '#FAF6EE' }}>
                <td className="p-2 font-bold" colSpan={6}>الرصيد الختامي</td>
                <td className="p-2 font-extrabold" style={{ color: close.color }}>{formatIQD(closing)}</td>
                <td className="p-2" colSpan={3}>{close.hint}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </Modal>
  );
}
